import {useState} from 'react';
import './ReviewStep.scss';
import {t} from 'i18next';
import {Box, Typography} from '@mui/material';
import ArrButton from '../../../../common/arr-button/ArrButton';
import ArrTextField from '../../../../common/arr-text-field/ArrTextField';
import {
  validateEmail,
  normalizeEmail,
} from '../../../../ai/investigate/helpers/aihelper';
import {ReactComponent as DeleteIcon} from '../../../../../assets/images/delete.svg';
import ArrIconButton from '../../../../common/arr-icon-button/ArrIconButton';
import {ArrDrawer} from '../../../../common/arr-drawer/ArrDrawer';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import {ToastMessageConfigType} from '../../../../../api/types/ingestionApi.type';
import {ToastType} from '../../../../../shared/constants';
import {useDispatch, useSelector} from 'react-redux';
import {setNotificationEmails} from '../../../../../redux/slice/createCustomIndexSlice';
import {RootState} from '../../../../../redux/store';

const ReviewStep = () => {
  const dispatch = useDispatch();

  const emails = useSelector(
    (state: RootState) =>
      state.indexManagement?.createCustomIndex?.NotificationEmails || []
  );
  const {isReadOnlyView} = useSelector(
    (state: RootState) => state.indexManagement
  );

  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailInput, setEmailInput] = useState('');
  const [openPanel, setOpenPanel] = useState<boolean>(false);
  const [toastMsgConfig, setToastMsgConfig] = useState<
    ToastMessageConfigType & {show: boolean}
  >({
    show: false,
  });

  const addEmail = () => {
    const trimmed = emailInput.trim();
    if (!trimmed) return;

    const err = validateEmail(trimmed);
    if (err) {
      setEmailError(err);
      return;
    }

    const normalized = normalizeEmail(trimmed);

    const updated = [
      ...emails?.filter((e: any) => e !== normalized),
      normalized,
    ];

    dispatch(setNotificationEmails(updated));

    setEmailInput('');
    setEmailError(null);
  };

  const removeEmail = (email: string) => {
    const updated = emails.filter((e: any) => e !== email);
    dispatch(setNotificationEmails(updated));
  };

  const fields = [
    {field: 'Field Name 1', analyzer: 'Analyzer Name'},
    {field: 'Field Name 2', analyzer: 'Analyzer Name'},
    {field: 'Field Name 3', analyzer: 'Analyzer Name'},
    {field: 'Field Name 4', analyzer: 'Analyzer Name'},
    {field: 'Field Name 5', analyzer: 'Analyzer Name'},
    {field: 'Field Name A', analyzer: 'Analyzer Name'},
    {field: 'Field Name B', analyzer: 'Analyzer Name'},
    {field: 'Field Name C', analyzer: 'Analyzer Name'},
    {field: 'Field Name D', analyzer: 'Analyzer Name'},
    {field: 'Field Name E', analyzer: 'Analyzer Name'},
    {field: 'Field Name F', analyzer: 'Analyzer Name'},
    {field: 'Field Name G', analyzer: 'Analyzer Name'},
    {field: 'Field Name 7', analyzer: 'Analyzer Name'},
    {field: 'Field Name 8', analyzer: 'Analyzer Name'},
    {field: 'Field Name 8', analyzer: 'Analyzer Name'},
    {field: 'Field Name 9', analyzer: 'Analyzer Name'},
  ];

  const getSortedFields = () => {
    return [...fields].sort((a, b) => a.field.localeCompare(b.field));
  };

  const firstFourFields = getSortedFields().slice(0, 4);

  return (
    <Box className="review-wrapper">
      <Box className="review-card">
        <Box className="review-header">
          <Typography
            className="typo-text subhead2 review-title"
            aria-label={t('indexManagement.customIndex.review.header')}
            aria-live="polite"
            aria-atomic="false"
            data-testid={`review-step-header`}
          >
            {t('indexManagement.customIndex.review.header')}
          </Typography>
        </Box>

        <Box className="review-grid">
          <Box className="left-section">
            <Box className="field-block">
              <Typography
                className="typo-text body2_size label"
                aria-label={t('indexManagement.customIndex.review.indexName')}
                aria-live="polite"
                aria-atomic="false"
                data-testid={`review-step-index-name`}
              >
                {t('indexManagement.customIndex.review.indexName')}
              </Typography>
              <Typography
                className="typo-text subhead4_size value"
                aria-label={t('indexManagement.customIndex.review.indexName')}
                aria-live="polite"
                aria-atomic="false"
                data-testid={`review-step-my-custom-index`}
              >
                MyCustomIndex
              </Typography>
            </Box>

            <Box className="field-block padding-top">
              <Typography
                className="typo-text body2_size label"
                aria-label={t('indexManagement.customIndex.review.source')}
                aria-live="polite"
                aria-atomic="false"
                data-testid={`review-step-source`}
              >
                {t('indexManagement.customIndex.review.source')}
              </Typography>
              <Typography
                className="typo-text subhead4_size value"
                aria-label={t('indexManagement.customIndex.review.source')}
                aria-live="polite"
                aria-atomic="false"
                data-testid={`review-step-saved-search4823719`}
              >
                SavedSearch4823719
              </Typography>
            </Box>
            <Box className="field-block-sub padding-top">
              <Box className="field-block-part">
                <Typography
                  className="typo-text body2_size label"
                  aria-label={t('indexManagement.customIndex.review.docs')}
                  aria-live="polite"
                  aria-atomic="false"
                  data-testid={`review-step-docs-label`}
                >
                  {t('indexManagement.customIndex.review.docs')}
                </Typography>
                <Typography
                  className="typo-text subhead4_size link"
                  aria-label={t('indexManagement.customIndex.review.docs')}
                  aria-live="polite"
                  aria-atomic="false"
                  data-testid={`review-step-docs-value`}
                >
                  125,000 Docs
                </Typography>
              </Box>
              <Box className="field-block-part">
                <Typography
                  className="typo-text body4_size label"
                  aria-label={t('indexManagement.customIndex.review.secGroup1')}
                  aria-live="polite"
                  aria-atomic="false"
                  data-testid={`review-step-sec-group1`}
                >
                  {t('indexManagement.customIndex.review.secGroup1')}:
                </Typography>
                <Typography
                  className="typo-text label3_size value"
                  aria-label={t('indexManagement.customIndex.review.secGroup1')}
                  aria-live="polite"
                  aria-atomic="false"
                  data-testid={`review-step-sec-group1-value`}
                >
                  145,000
                </Typography>
              </Box>
              <Box className="field-block-part padding-bottom">
                <Typography
                  className="typo-text body4_size label"
                  aria-label={t('indexManagement.customIndex.review.secGroup2')}
                  aria-live="polite"
                  aria-atomic="false"
                  data-testid={`review-step-sec-group2`}
                >
                  {t('indexManagement.customIndex.review.secGroup2')}:
                </Typography>
                <Typography
                  className="typo-text label3_size value"
                  aria-label={t('indexManagement.customIndex.review.secGroup2')}
                  aria-live="polite"
                  aria-atomic="false"
                  data-testid={`review-step-sec-group2-value`}
                >
                  130,000
                </Typography>
              </Box>
            </Box>
            <Box className="field-block padding-top">
              <Typography
                className="typo-text body2_size label"
                aria-label={t('indexManagement.customIndex.review.secGroup')}
                aria-live="polite"
                aria-atomic="false"
                data-testid={`review-step-sec-group`}
              >
                {t('indexManagement.customIndex.review.secGroup')}
              </Typography>
              <Typography
                className="typo-text subhead4_size value"
                aria-label={t('indexManagement.customIndex.review.secGroup')}
                aria-live="polite"
                aria-atomic="false"
                data-testid={`review-step-selected-security-group`}
              >
                Selected Security Group
                <Typography
                  component="span"
                  className="typo-text body3_size label"
                  aria-label={t('indexManagement.customIndex.review.secGroup2')}
                  aria-live="polite"
                  aria-atomic="false"
                  data-testid={`review-step-count-users`}
                >
                  {' '}
                  (42 users)
                </Typography>
              </Typography>
            </Box>

            <Box className="field-block padding-top no-border">
              <Typography
                className="typo-text body2_size label"
                aria-label={t('indexManagement.customIndex.review.fields')}
                aria-live="polite"
                aria-atomic="false"
                data-testid={`review-step-fields`}
              >
                {t('indexManagement.customIndex.review.fields')}
              </Typography>
              <Box className="fields-list">
                {firstFourFields.map((f, i) => (
                  <Typography
                    key={'field' + i}
                    className="typo-text subhead4_size value"
                    aria-label={f.field}
                    aria-live="polite"
                    aria-atomic="false"
                    data-testid={`review-step-field-${i}`}
                  >
                    {f.field}
                  </Typography>
                ))}
                <Box className="filed-button">
                  <ArrButton
                    variant="text"
                    aria-label={t('indexManagement.customIndex.review.showAll')}
                    aria-live="polite"
                    aria-atomic="false"
                    size="small"
                    onClick={() => setOpenPanel(true)}
                    dataTestId="review-step-show-all-button"
                  >
                    {t('indexManagement.customIndex.review.showAll')}
                  </ArrButton>
                </Box>
              </Box>
            </Box>
          </Box>

          {!isReadOnlyView && (
            <Box className="right-section">
              <Box className="email-block">
                <Box className="email-head">
                  <Typography
                    component="span"
                    aria-label={t(
                      'ai.investigate.createEditIndex.create.emailHeader'
                    )}
                    aria-live="polite"
                    aria-atomic="false"
                    className="typo-text subhead3_size"
                    data-testid={`review-step-email-header`}
                  >
                    {t('ai.investigate.createEditIndex.create.emailHeader')}
                  </Typography>
                  <Typography
                    component="span"
                    aria-label={t(
                      'ai.investigate.createEditIndex.create.emailOptional'
                    )}
                    aria-live="polite"
                    aria-atomic="false"
                    className="typo-text subhead3_size label"
                    data-testid={`review-step-optional-text`}
                  >
                    {' ' +
                      t('ai.investigate.createEditIndex.create.emailOptional')}
                  </Typography>
                </Box>
                <Box className="email-subhead">
                  <Typography
                    component="span"
                    aria-label={t(
                      'ai.investigate.createEditIndex.create.emailSubHeader'
                    )}
                    aria-live="polite"
                    aria-atomic="false"
                    className="typo-text body2_size"
                    data-testid={`review-step-email-sub-header`}
                  >
                    {t('ai.investigate.createEditIndex.create.emailSubHeader')}
                  </Typography>
                </Box>
                <Box className="email-row">
                  <ArrTextField
                    className="email-text-field"
                    id="enabled-focus"
                    state={emailError ? 'error' : 'enabled'}
                    aria-label={t(
                      'ai.investigate.createEditIndex.create.emailAddress'
                    )}
                    aria-live="polite"
                    aria-atomic="false"
                    label={t(
                      'ai.investigate.createEditIndex.create.emailAddress'
                    )}
                    dataTestId="review-step-email"
                    supportText={emailError || ''}
                    value={emailInput}
                    onChange={(e) => {
                      const val = e.target.value;
                      setEmailInput(val);
                      if (!val.trim()) {
                        setEmailError(null);
                        return;
                      }
                      const err = validateEmail(val.trim());
                      if (err) {
                        setEmailError(err);
                      } else {
                        setEmailError(null);
                      }
                    }}
                    showErrorIcon
                  />

                  <ArrButton
                    variant="text"
                    aria-label={t('common.add')}
                    aria-live="polite"
                    aria-atomic="false"
                    size="large"
                    onClick={addEmail}
                    disabled={!emailInput.trim() || !!emailError}
                    dataTestId="review-step-add-button"
                  >
                    {t('common.add')}
                  </ArrButton>
                </Box>

                <Box className="email-list">
                  {emails?.map((email: any, index) => (
                    <Box className="email-item" key={email}>
                      <Typography
                        className="typo-text body2_size"
                        aria-label={email}
                        aria-live="polite"
                        aria-atomic="false"
                        data-testid={`review-step-email-${index}`}
                      >
                        {email}
                      </Typography>
                      <ArrIconButton
                        testId={`arr-delete-icon`}
                        aria-label={'Delete'}
                        aria-live="polite"
                        aria-atomic="false"
                        onClick={() => removeEmail(email)}
                        icon={<DeleteIcon />}
                        ariaLabel={'delete-icon'}
                        shape="none"
                      ></ArrIconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
        {!isReadOnlyView && (
          <Box className="footer-content">
            <Box className="footer-banner">
              <Typography
                className="typo-text body3_size footer-text"
                aria-label={t('indexManagement.customIndex.review.footer')}
                aria-live="polite"
                aria-atomic="false"
                data-testid={`review-step-footer`}
              >
                {t('indexManagement.customIndex.review.footer')}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      {openPanel && (
        <ArrDrawer
          aria-label={t(
            'ai.investigate.createEditIndex.create.createIndexTitle'
          )}
          aria-live="polite"
          aria-atomic="false"
          isOpen={openPanel}
          onClose={() => setOpenPanel(false)}
          showButton={false}
          showHeader={false}
          title={t('ai.investigate.createEditIndex.create.createIndexTitle')}
          size="small"
          side="right"
          data-testid="createindex-drawer"
        >
          <Box className="fields-drawer-container">
            <Box className="fields-drawer-header-box">
              <Box className="fields-drawer-row">
                <Typography
                  component="h3"
                  role="header"
                  aria-label={t('indexManagement.customIndex.review.fields')}
                  aria-live="polite"
                  aria-atomic="false"
                  className="typo-text headline3_size"
                  data-testid={`review-step-fields-text`}
                >
                  {t('indexManagement.customIndex.review.fields')}
                </Typography>
                <Box className="fields-drawer-buttons-box">
                  <ArrButton
                    aria-label={t('common.close')}
                    aria-live="polite"
                    aria-atomic="false"
                    variant="secondary"
                    size="medium"
                    onClick={() => setOpenPanel(false)}
                    disabled={false}
                    dataTestId="review-step-close-button"
                    className="close-button"
                  >
                    {t('common.close')}
                  </ArrButton>
                </Box>
              </Box>
            </Box>
            <Box className="fields-drawer-body">
              <Box className="table-header">
                <Typography
                  className="typo-text label2_size"
                  aria-label={t('indexManagement.customIndex.review.fieldName')}
                  aria-live="polite"
                  aria-atomic="false"
                  data-testid={`review-step-field-name`}
                >
                  {t('indexManagement.customIndex.review.fieldName')}
                </Typography>
                <Typography
                  className="typo-text label2_size"
                  aria-label={t('indexManagement.customIndex.review.analyzer')}
                  aria-live="polite"
                  aria-atomic="false"
                  data-testid={`review-step-analyzer`}
                >
                  {t('indexManagement.customIndex.review.analyzer')}
                </Typography>
              </Box>
              <Box className="table-content">
                {getSortedFields().map((f, i) => (
                  <Box key={'fields' + i} className="field-row-item">
                    <Typography
                      className="typo-text body3_size"
                      aria-label={f.field}
                      aria-live="polite"
                      aria-atomic="false"
                      data-testid={`review-step-sorted-field-${i}`}
                    >
                      {f.field}
                    </Typography>
                    <Typography
                      className="typo-text body3_size"
                      aria-label={f.analyzer}
                      aria-live="polite"
                      aria-atomic="false"
                      data-testid={`review-step-analyzer-${i}`}
                    >
                      {f.analyzer}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </ArrDrawer>
      )}
      {toastMsgConfig.show && (
        <ArrToastNotification
          onClose={() => setToastMsgConfig({show: false})}
          status={[toastMsgConfig.type ?? ToastType.Success]}
          message={toastMsgConfig.message ?? ''}
          title={toastMsgConfig.title}
        />
      )}
    </Box>
  );
};

export default ReviewStep;
